import React, { useState, useEffect } from 'react';
import axios from 'axios';
import configManager from './ConfigurationManager';

const ConfigInterface = () => {
  const [config, setConfig] = useState(() => configManager.getConfig());
  const [activeTab, setActiveTab] = useState('features');
  const [dbLocations, setDbLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [newPromotion, setNewPromotion] = useState({
    name: "",
    type: "happyHours",
    days: [],
    startTime: "08:00",
    endTime: "10:00",
    discount: 50,
    categories: [],
    products: [],
    isActive: true,
    showInAddition: true
  });
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [newLocation, setNewLocation] = useState({
    id: "",
    name: "",
    address: "",
    type: "restaurant",
    hasTables: true
  });

  // Recharger la configuration
  const reloadConfig = () => {
    setConfig(configManager.getConfig());
  };

  // Charger les localisations de la DB
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await axios.get('/api/Locationspin/getAllLocations');
        console.log("📍 Localisations récupérées de la DB:", response.data);
        setDbLocations(response.data);
      } catch (error) {
        console.error("❌ Erreur lors du chargement des localisations:", error);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // Mettre à jour la configuration
  const updateNestedConfig = (path, value) => {
    // Synchronisation automatique des boutons de table
    if (path === 'features.tableSystem.enabled') {
      configManager.updateConfig('restaurant.hasTables', value);
    } else if (path === 'restaurant.hasTables') {
      configManager.updateConfig('features.tableSystem.enabled', value);
    }

    configManager.updateConfig(path, value);
    reloadConfig();
  };

  // Gestion des promotions
  const handleDeletePromotion = (type, index) => {
    const admin = configManager.getAdminInterface();
    if (admin.deletePromotion(type, index)) {
      reloadConfig();
    }
  };

  const handleTogglePromotion = (type, index) => {
    const admin = configManager.getAdminInterface();
    if (admin.togglePromotion(type, index)) {
      reloadConfig();
    }
  };

  const handleToggleShowInAddition = (type, index) => {
    const admin = configManager.getAdminInterface();
    if (admin.toggleShowInAddition(type, index)) {
      reloadConfig();
    }
  };

  const handleAddPromotion = () => {
    const admin = configManager.getAdminInterface();
    admin.addPromotion(newPromotion.type, newPromotion);
    reloadConfig();
    setShowPromotionDialog(false);
    resetNewPromotion();
  };

  const handleUpdatePromotion = () => {
    if (editingPromotion) {
      const admin = configManager.getAdminInterface();
      admin.updatePromotion(editingPromotion.type, editingPromotion.index, {
        name: editingPromotion.name,
        days: editingPromotion.days,
        startTime: editingPromotion.startTime,
        endTime: editingPromotion.endTime,
        discount: editingPromotion.discount,
        categories: editingPromotion.categories,
        products: editingPromotion.products,
        isActive: editingPromotion.isActive,
        showInAddition: editingPromotion.showInAddition
      });
      reloadConfig();
      setEditingPromotion(null);
      setShowPromotionDialog(false);
    }
  };

  const resetNewPromotion = () => {
    setNewPromotion({
      name: "",
      type: "happyHours",
      days: [],
      startTime: "08:00",
      endTime: "10:00",
      discount: 50,
      categories: [],
      products: [],
      isActive: true,
      showInAddition: true
    });
  };

  // Gestion des locations
  const handleAddLocation = () => {
    if (newLocation.id && newLocation.name) {
      const currentLocations = config.restaurant?.locations || [];
      const updatedLocations = [...currentLocations, newLocation];
      updateNestedConfig('restaurant.locations', updatedLocations);
      setNewLocation({
        id: "",
        name: "",
        address: "",
        type: "restaurant",
        hasTables: true
      });
      setShowLocationDialog(false);
    }
  };

  const handleDeleteLocation = (index) => {
    const currentLocations = config.restaurant?.locations || [];
    const updatedLocations = currentLocations.filter((_, i) => i !== index);
    updateNestedConfig('restaurant.locations', updatedLocations);
  };

  // Styles
  const styles = {
    container: {
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '20px'
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '30px',
      borderRadius: '12px',
      marginBottom: '30px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
    },
    tabContainer: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      background: 'white',
      borderRadius: '10px',
      padding: '10px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    },
    tab: {
      flex: 1,
      padding: '15px 20px',
      border: 'none',
      borderRadius: '8px',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    },
    activeTab: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      padding: '25px',
      marginBottom: '25px',
      boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e0e0e0'
    },
    cardTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
      color: '#333',
      fontSize: '18px',
      fontWeight: '600'
    },
    switchContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '15px 0',
      borderBottom: '1px solid #f0f0f0'
    },
    button: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonSecondary: {
      background: '#6c757d',
      color: 'white',
      border: 'none',
      padding: '10px 20px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.3s ease'
    },
    buttonSmall: {
      background: '#6c757d',
      color: 'white',
      border: 'none',
      padding: '6px 12px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      transition: 'all 0.3s ease'
    },
    chip: {
      display: 'inline-block',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      margin: '4px',
      background: '#e9ecef',
      color: '#495057',
      border: '1px solid #dee2e6'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '14px',
      marginTop: '8px',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '14px',
      marginTop: '8px',
      background: 'white',
      boxSizing: 'border-box'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: 'white',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalHeader: {
      padding: '20px',
      borderBottom: '1px solid #eee',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalContent: {
      padding: '20px'
    },
    modalActions: {
      padding: '20px',
      borderTop: '1px solid #eee',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px'
    },
    slider: {
      width: '100%',
      marginTop: '20px'
    }
  };

  const Switch = ({ checked, onChange }) => (
    <label style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ opacity: 0, width: 0, height: 0 }}
      />
      <span style={{
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: checked ? '#667eea' : '#ccc',
        transition: '.4s',
        borderRadius: '34px'
      }}>
        <span style={{
          position: 'absolute',
          height: '26px',
          width: '26px',
          left: '4px',
          bottom: '4px',
          backgroundColor: 'white',
          transition: '.4s',
          borderRadius: '50%',
          transform: checked ? 'translateX(26px)' : 'none'
        }} />
      </span>
    </label>
  );

  const Slider = ({ value, min, max, onChange, label }) => (
    <div style={styles.slider}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontWeight: '500' }}>{label}</span>
        <span style={{ color: '#667eea', fontWeight: 'bold' }}>{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        style={{
          width: '100%',
          height: '6px',
          borderRadius: '3px',
          background: '#ddd',
          outline: 'none'
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>{min}</span>
        <span style={{ fontSize: '12px', color: '#666' }}>{max}</span>
      </div>
    </div>
  );

  const renderLocalizationTab = () => (
    <div>
      <h2 style={{ color: '#333', marginBottom: '25px', fontSize: '24px' }}>
        📍 Gestion de la Localisation
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        {/* Configuration du Terminal */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>🖥️</span>
            <span>Configuration du Terminal Actuel</span>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
              Localisation de ce terminal
            </label>
            <select
              value={configManager.getCurrentLocationId()}
              onChange={(e) => {
                configManager.setLocation(e.target.value);
                reloadConfig();
              }}
              style={styles.select}
            >
              <option value="default">Configuration Globale / Par Défaut</option>
              {dbLocations.map(loc => (
                <option key={loc._id} value={loc._id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{
            padding: '15px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#1976d2',
            borderLeft: '4px solid #1976d2'
          }}>
            <strong>Note:</strong> Les réglages modifiés dans les autres onglets seront désormais spécifiques à <strong>{
              dbLocations.find(l => l._id === configManager.getCurrentLocationId())?.name || "la configuration globale"
            }</strong>.
          </div>
        </div>

        {/* Liste des Locations */}
        <div style={styles.card}>
          <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>🌍</span>
              <span>Points de Vente (Base de Données)</span>
            </div>
          </div>

          <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
            {loadingLocations ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Chargement des localisations...</div>
            ) : dbLocations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Aucune localisation trouvée dans la base de données.</div>
            ) : dbLocations.map((location, index) => (
              <div key={location._id || index} style={{
                padding: '12px',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                background: configManager.getCurrentLocationId() === location._id ? '#f0f4ff' : '#f8f9fa',
                borderLeft: configManager.getCurrentLocationId() === location._id ? '4px solid #667eea' : '1px solid #e0e0e0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong>{location.name}</strong>
                      {configManager.getCurrentLocationId() === location._id &&
                        <span style={{ fontSize: '10px', backgroundColor: '#667eea', color: 'white', padding: '2px 6px', borderRadius: '10px' }}>ACTIF</span>
                      }
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>ID: {location._id}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  const renderFeaturesTab = () => (
    <div>
      <h2 style={{ color: '#333', marginBottom: '25px', fontSize: '24px' }}>
        ⚙️ Fonctionnalités Principales
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        {/* Système de Tables */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>📊</span>
            <span>Système de Tables</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Activer le système de tables</span>
            <Switch
              checked={config.features?.tableSystem?.enabled || false}
              onChange={(e) => updateNestedConfig('features.tableSystem.enabled', e.target.checked)}
            />
          </div>

          {config.features?.tableSystem?.enabled && (
            <>
              <div style={styles.switchContainer}>
                <span>Transfert de tables</span>
                <Switch
                  checked={config.features.tableSystem.allowTableTransfer}
                  onChange={(e) => updateNestedConfig('features.tableSystem.allowTableTransfer', e.target.checked)}
                />
              </div>

              <div style={styles.switchContainer}>
                <span>Verrouillage de tables</span>
                <Switch
                  checked={config.features.tableSystem.allowTableLock}
                  onChange={(e) => updateNestedConfig('features.tableSystem.allowTableLock', e.target.checked)}
                />
              </div>

              <div style={{ marginTop: '20px' }}>
                <Slider
                  value={config.features.tableSystem.maxTables}
                  min={1}
                  max={200} // CHANGÉ À 200
                  onChange={(value) => updateNestedConfig('features.tableSystem.maxTables', value)}
                  label="Nombre maximum de tables"
                />
              </div>
            </>
          )}
        </div>

        {/* Paiement */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>💳</span>
            <span>Système de Paiement</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Activer les paiements</span>
            <Switch
              checked={config.features?.payment?.enabled || false}
              onChange={(e) => updateNestedConfig('features.payment.enabled', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Paiement partiel autorisé</span>
            <Switch
              checked={config.features?.payment?.allowPartialPayment || false}
              onChange={(e) => updateNestedConfig('features.payment.allowPartialPayment', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Impression automatique des reçus</span>
            <Switch
              checked={config.features?.payment?.autoPrint || false}
              onChange={(e) => updateNestedConfig('features.payment.autoPrint', e.target.checked)}
            />
          </div>
        </div>

        {/* Personnel */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>👥</span>
            <span>Addition Personnel</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Activer l'addition personnel</span>
            <Switch
              checked={config.features?.staff?.enabled || false}
              onChange={(e) => updateNestedConfig('features.staff.enabled', e.target.checked)}
            />
          </div>

          {config.features?.staff?.enabled && (
            <>
              <div style={{ marginTop: '20px' }}>
                <Slider
                  value={config.features.staff.discountPercentage}
                  min={0}
                  max={100}
                  onChange={(value) => updateNestedConfig('features.staff.discountPercentage', value)}
                  label="Pourcentage de réduction"
                />
              </div>

              <div style={styles.switchContainer}>
                <span>Sélection du personnel requise</span>
                <Switch
                  checked={config.features.staff.requireStaffSelection}
                  onChange={(e) => updateNestedConfig('features.staff.requireStaffSelection', e.target.checked)}
                />
              </div>
            </>
          )}
        </div>

        {/* Annulation */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>❌</span>
            <span>Système d'Annulation</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Activer l'annulation</span>
            <Switch
              checked={config.features?.cancellation?.enabled || false}
              onChange={(e) => updateNestedConfig('features.cancellation.enabled', e.target.checked)}
            />
          </div>

          {config.features?.cancellation?.enabled && (
            <>
              <div style={styles.switchContainer}>
                <span>Restauration du stock</span>
                <Switch
                  checked={config.features.cancellation.allowStockRestoration}
                  onChange={(e) => updateNestedConfig('features.cancellation.allowStockRestoration', e.target.checked)}
                />
              </div>

              <div style={styles.switchContainer}>
                <span>Raison d'annulation requise</span>
                <Switch
                  checked={config.features.cancellation.requireCancellationReason}
                  onChange={(e) => updateNestedConfig('features.cancellation.requireCancellationReason', e.target.checked)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderUITab = () => (
    <div>
      <h2 style={{ color: '#333', marginBottom: '25px', fontSize: '24px' }}>
        🎨 Interface Utilisateur
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>👁️</span>
            <span>Sections Visibles</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {Object.entries(config.ui?.visibleSections || {}).map(([section, visible]) => (
              <div key={section} style={styles.switchContainer}>
                <span style={{ fontSize: '14px' }}>
                  {section === 'tablesSection' ? 'Tables' :
                    section === 'categoriesSection' ? 'Catégories' :
                      section === 'productsSection' ? 'Produits' :
                        section === 'additionSection' ? 'Panier' :
                          section === 'paymentSection' ? 'Paiement' :
                            section === 'staffSection' ? 'Personnel' :
                              section === 'cancelSection' ? 'Annulation' :
                                section === 'discountSection' ? 'Réductions' : section}
                </span>
                <Switch
                  checked={visible}
                  onChange={(e) => updateNestedConfig(`ui.visibleSections.${section}`, e.target.checked)}
                />
              </div>
            ))}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>🏪</span>
            <span>Configuration du Restaurant</span>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#666' }}>
              Type de Restaurant
            </label>
            <select
              value={config.restaurant?.type || "restaurant"}
              onChange={(e) => updateNestedConfig('restaurant.type', e.target.value)}
              style={styles.select}
            >
              <option value="restaurant">Restaurant (avec tables)</option>
              <option value="foodtrack">Foodtrack (vente directe)</option>
              <option value="cafe">Café</option>
              <option value="boutique">Boutique</option>
            </select>
          </div>

          <div style={styles.switchContainer}>
            <span>Gérer les tables</span>
            <Switch
              checked={config.restaurant?.hasTables || false}
              onChange={(e) => updateNestedConfig('restaurant.hasTables', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Service livraison</span>
            <Switch
              checked={config.restaurant?.hasDelivery || false}
              onChange={(e) => updateNestedConfig('restaurant.hasDelivery', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Service à emporter</span>
            <Switch
              checked={config.restaurant?.hasTakeAway || false}
              onChange={(e) => updateNestedConfig('restaurant.hasTakeAway', e.target.checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPromotionsTab = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h2 style={{ color: '#333', fontSize: '24px' }}>
          ⏰ Gestion des Promotions
        </h2>
        <button style={styles.button} onClick={() => setShowPromotionDialog(true)}>
          <span style={{ fontSize: '18px' }}>+</span>
          Nouvelle Promotion
        </button>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '25px'
      }}>
        <p style={{ margin: 0, color: '#1976d2', fontWeight: '500' }}>
          Les promotions sont appliquées automatiquement selon les conditions définies ci-dessous.
          Activez "Afficher dans l'addition" pour que les clients voient les promotions applicables.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>⚙️</span>
            <span>Paramètres Généraux</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Activer les promotions</span>
            <Switch
              checked={config.promotions?.settings?.enablePromotions || false}
              onChange={(e) => updateNestedConfig('promotions.settings.enablePromotions', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Application automatique</span>
            <Switch
              checked={config.promotions?.settings?.autoApply || false}
              onChange={(e) => updateNestedConfig('promotions.settings.autoApply', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Afficher dans l'addition</span>
            <Switch
              checked={config.promotions?.settings?.showInAddition !== false}
              onChange={(e) => updateNestedConfig('promotions.settings.showInAddition', e.target.checked)}
            />
          </div>

          <div style={{ marginTop: '20px' }}>
            <Slider
              value={config.promotions?.settings?.maxDiscount || 70}
              min={0}
              max={100}
              onChange={(value) => updateNestedConfig('promotions.settings.maxDiscount', value)}
              label="Réduction maximale autorisée (%)"
            />
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.cardTitle, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>🎯</span>
              <span>Happy Hours Actives</span>
            </div>
            <span style={{
              background: '#667eea',
              color: 'white',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {config.promotions?.happyHours?.filter(hh => hh.isActive).length || 0} active(s)
            </span>
          </div>

          {config.promotions?.happyHours?.map((hh, index) => (
            <div key={hh.id || index} style={{
              border: `2px solid ${hh.isActive ? '#667eea' : '#ddd'}`,
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '15px',
              background: hh.isActive ? '#f0f5ff' : '#f8f9fa'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                    <strong>{hh.name}</strong>
                    {!hh.isActive && (
                      <span style={{
                        background: '#6c757d',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px'
                      }}>Inactive</span>
                    )}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '3px' }}>
                    {hh.startTime} - {hh.endTime} | Réduction: {hh.discount}%
                  </div>
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    Catégories: {hh.categories?.join(', ') || 'Toutes'}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#667eea',
                      fontSize: '16px'
                    }}
                    onClick={() => {
                      setEditingPromotion({ ...hh, type: 'happyHours', index });
                      setShowPromotionDialog(true);
                    }}
                    title="Modifier"
                  >
                    ✏️
                  </button>
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#dc3545',
                      fontSize: '16px'
                    }}
                    onClick={() => handleDeletePromotion('happyHours', index)}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Switch
                    checked={hh.isActive}
                    onChange={() => handleTogglePromotion('happyHours', index)}
                  />
                  <span style={{ fontWeight: '500' }}>Active</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Switch
                    checked={hh.showInAddition !== false}
                    onChange={() => handleToggleShowInAddition('happyHours', index)}
                  />
                  <span style={{ fontWeight: '500' }}>Afficher dans addition</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div>
      <h2 style={{ color: '#333', marginBottom: '25px', fontSize: '24px' }}>
        ⚡ Paramètres Avancés
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>🔄</span>
            <span>Actions Automatiques</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Actualisation automatique des tables</span>
            <Switch
              checked={config.advanced?.autoActions?.autoRefreshTables || false}
              onChange={(e) => updateNestedConfig('advanced.autoActions.autoRefreshTables', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Sauvegarde automatique des factures</span>
            <Switch
              checked={config.advanced?.autoActions?.autoSaveBill || false}
              onChange={(e) => updateNestedConfig('advanced.autoActions.autoSaveBill', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Sauvegarde automatique des données</span>
            <Switch
              checked={config.advanced?.autoActions?.autoBackup || false}
              onChange={(e) => updateNestedConfig('advanced.autoActions.autoBackup', e.target.checked)}
            />
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>
            <span style={{ fontSize: '20px' }}>🔒</span>
            <span>Sécurité</span>
          </div>

          <div style={styles.switchContainer}>
            <span>Connexion requise</span>
            <Switch
              checked={config.advanced?.security?.requireLogin || false}
              onChange={(e) => updateNestedConfig('advanced.security.requireLogin', e.target.checked)}
            />
          </div>

          <div style={styles.switchContainer}>
            <span>Journalisation des actions</span>
            <Switch
              checked={config.advanced?.security?.logAllActions || false}
              onChange={(e) => updateNestedConfig('advanced.security.logAllActions', e.target.checked)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPromotionDialog = () => {
    const promotion = editingPromotion || newPromotion;

    return showPromotionDialog && (
      <div style={styles.modalOverlay}>
        <div style={styles.modal}>
          <div style={styles.modalHeader}>
            <h3 style={{ margin: 0 }}>
              {editingPromotion ? 'Modifier la Promotion' : 'Nouvelle Promotion'}
            </h3>
            <button
              onClick={() => {
                setShowPromotionDialog(false);
                setEditingPromotion(null);
                resetNewPromotion();
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ✕
            </button>
          </div>

          <div style={styles.modalContent}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Type de promotion
              </label>
              <select
                value={promotion.type}
                onChange={(e) => {
                  if (editingPromotion) {
                    setEditingPromotion({ ...promotion, type: e.target.value });
                  } else {
                    setNewPromotion({ ...promotion, type: e.target.value });
                  }
                }}
                style={styles.select}
              >
                <option value="happyHours">Happy Hour</option>
                <option value="datePromotions">Promotion par date</option>
                <option value="permanentPromotions">Promotion permanente</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Nom de la promotion
              </label>
              <input
                type="text"
                value={promotion.name}
                onChange={(e) => {
                  if (editingPromotion) {
                    setEditingPromotion({ ...promotion, name: e.target.value });
                  } else {
                    setNewPromotion({ ...promotion, name: e.target.value });
                  }
                }}
                style={styles.input}
                placeholder="Ex: Happy Hour Matin"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Heure de début
                </label>
                <input
                  type="time"
                  value={promotion.startTime}
                  onChange={(e) => {
                    if (editingPromotion) {
                      setEditingPromotion({ ...promotion, startTime: e.target.value });
                    } else {
                      setNewPromotion({ ...promotion, startTime: e.target.value });
                    }
                  }}
                  style={styles.input}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Heure de fin
                </label>
                <input
                  type="time"
                  value={promotion.endTime}
                  onChange={(e) => {
                    if (editingPromotion) {
                      setEditingPromotion({ ...promotion, endTime: e.target.value });
                    } else {
                      setNewPromotion({ ...promotion, endTime: e.target.value });
                    }
                  }}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Pourcentage de réduction
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={promotion.discount}
                onChange={(e) => {
                  const value = Math.min(parseInt(e.target.value) || 0, 100);
                  if (editingPromotion) {
                    setEditingPromotion({ ...promotion, discount: value });
                  } else {
                    setNewPromotion({ ...promotion, discount: value });
                  }
                }}
                style={styles.input}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <Switch
                checked={promotion.isActive}
                onChange={(e) => {
                  if (editingPromotion) {
                    setEditingPromotion({ ...promotion, isActive: e.target.checked });
                  } else {
                    setNewPromotion({ ...promotion, isActive: e.target.checked });
                  }
                }}
              />
              <span style={{ fontWeight: '500' }}>Promotion active</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <Switch
                checked={promotion.showInAddition !== false}
                onChange={(e) => {
                  if (editingPromotion) {
                    setEditingPromotion({ ...promotion, showInAddition: e.target.checked });
                  } else {
                    setNewPromotion({ ...promotion, showInAddition: e.target.checked });
                  }
                }}
              />
              <span style={{ fontWeight: '500' }}>Afficher dans l'addition</span>
            </div>
          </div>

          <div style={styles.modalActions}>
            <button
              onClick={() => {
                setShowPromotionDialog(false);
                setEditingPromotion(null);
                resetNewPromotion();
              }}
              style={styles.buttonSecondary}
            >
              Annuler
            </button>
            <button
              onClick={() => {
                if (editingPromotion) {
                  handleUpdatePromotion();
                } else {
                  handleAddPromotion();
                }
              }}
              style={styles.button}
            >
              💾 Sauvegarder
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLocationDialog = () => {
    return showLocationDialog && (
      <div style={styles.modalOverlay}>
        <div style={styles.modal}>
          <div style={styles.modalHeader}>
            <h3 style={{ margin: 0 }}>Nouvelle Location</h3>
            <button
              onClick={() => setShowLocationDialog(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ✕
            </button>
          </div>

          <div style={styles.modalContent}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                ID unique
              </label>
              <input
                type="text"
                value={newLocation.id}
                onChange={(e) => setNewLocation({ ...newLocation, id: e.target.value })}
                style={styles.input}
                placeholder="jm, tm, etc."
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Nom
              </label>
              <input
                type="text"
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                style={styles.input}
                placeholder="Ex: SPINTOSS JM"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Adresse
              </label>
              <input
                type="text"
                value={newLocation.address}
                onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                style={styles.input}
                placeholder="Ex: JARDINS D'EL MENZAH 2"
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Type
              </label>
              <select
                value={newLocation.type}
                onChange={(e) => setNewLocation({ ...newLocation, type: e.target.value })}
                style={styles.select}
              >
                <option value="restaurant">Restaurant</option>
                <option value="foodtrack">Foodtrack</option>
                <option value="cafe">Café</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Switch
                checked={newLocation.hasTables}
                onChange={(e) => setNewLocation({ ...newLocation, hasTables: e.target.checked })}
              />
              <span style={{ fontWeight: '500' }}>Gestion des tables</span>
            </div>
          </div>

          <div style={styles.modalActions}>
            <button
              onClick={() => setShowLocationDialog(false)}
              style={styles.buttonSecondary}
            >
              Annuler
            </button>
            <button
              onClick={handleAddLocation}
              style={styles.button}
            >
              💾 Ajouter
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'features', label: 'Fonctionnalités', icon: '⚙️', content: renderFeaturesTab() },
    { id: 'ui', label: 'Interface', icon: '🎨', content: renderUITab() },
    { id: 'localisation', label: 'Localisation', icon: '📍', content: renderLocalizationTab() },
    { id: 'promotions', label: 'Promotions', icon: '⏰', content: renderPromotionsTab() },
    { id: 'advanced', label: 'Avancé', icon: '⚡', content: renderAdvancedTab() },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '32px', fontWeight: 'bold' }}>
              ⚙️ Panneau de Configuration
            </h1>
            <p style={{ margin: '10px 0 0 0', opacity: 0.9, fontSize: '16px' }}>
              Gestion complète de l'application {config.restaurant?.name || 'SPINTOSS'}
            </p>
          </div>
          <button
            onClick={reloadConfig}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              backdropFilter: 'blur(10px)'
            }}
          >
            🔄 Actualiser
          </button>
        </div>
      </div>

      <div style={styles.tabContainer}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div style={styles.card}>
        {tabs.find(tab => tab.id === activeTab)?.content}
      </div>

      {renderPromotionDialog()}
    </div>
  );
};

export default ConfigInterface;